<script setup lang="ts">
import type { IGetAlbumFilters } from "@/api"
import type { IGalleryFilters, MediaTypeFilter } from "@/components/GalleryFiltersDrawer/galleryFilters.interface"
import type { Album, Comment, Media } from "@/models"
import type { ToastHtmlElement } from "@/types"
import { useInfiniteQuery, useQueryClient } from "@tanstack/vue-query"
import { useIntersectionObserver, useLocalStorage, useMediaQuery, useUrlSearchParams } from "@vueuse/core"
import { computed, ref, useTemplateRef, watchEffect } from "vue"
import { useRouter } from "vue-router"
import { listMedia, useGetAlbum, useGetEventVault, useListAlbums, usePinMedia } from "@/api"
import { AlbumSelector, CommentsDrawer, FlagContentModal, Footer, GalleryActions, GalleryFeed, GalleryFiltersDrawer, GalleryGrid, Lightbox, MainGalleryHiddenMessage, Uploader } from "@/components"
import { emitSelectedAlbum } from "@/components/Nav/AlbumIdEventBus"
import { AppLink, Banner, Drawer, Toast } from "@/components/ui"
import { useConvertedRouteParam, useFilters, useGalleryPermissions, useLightbox, useModal, useTranslation } from "@/composables"
import { GuestLayout } from "@/layouts"
import { useEventVaultStore } from "@/stores/eventVaultStore"
import { useTemplateStore } from "@/stores/templateStore"
import { CommentsDrawerKey, FlagContentModalKey, GalleryFiltersDrawerKey, UploadDrawerKey } from "@/symbols"
import { GalleryMode, SocialsBehavior, TaggingBehavior } from "@/types"

definePage({
  meta: {
    requiresCookies: true,
    pageLayout: "multi",
  },
})

const eventVaultId = useConvertedRouteParam("id", "/guest/[id]/gallery", value => Number(value))

const templateStore = useTemplateStore()
const eventVaultStore = useEventVaultStore()

const { t } = useTranslation()
const router = useRouter()
const queryClient = useQueryClient()

const { toggle: toggleFilters } = useModal(GalleryFiltersDrawerKey)
const { toggle: toggleUploader, isActive } = useModal(UploadDrawerKey)
const { toggle: toggleCommentDrawer, isActive: isCommentDrawerActive } = useModal(CommentsDrawerKey)
const { toggle: toggleFlagContentModal } = useModal(FlagContentModalKey)

const queryString = useUrlSearchParams<{ album?: unknown, upload?: unknown, mode?: GalleryMode }>()
const albumId = ref<number | undefined>()
const isMobileView = useMediaQuery("(max-width: 1024px)")
const galleryModePreference = useLocalStorage<GalleryMode | undefined>("gallery-mode", undefined)
const pendingApprovalBannerDismissed = useLocalStorage<boolean>("pending-approval-banner-dismissed", false)

function dismissPendingApprovalBanner() {
  pendingApprovalBannerDismissed.value = true
}

function getInitialMode(): GalleryMode {
  if (queryString.mode && typeof queryString.mode === "string") {
    return queryString.mode === "FEED" ? GalleryMode.feed : GalleryMode.grid
  }
  if (galleryModePreference.value) {
    return galleryModePreference.value
  }
  return isMobileView.value ? GalleryMode.feed : GalleryMode.grid
}

const mode = ref<GalleryMode>(getInitialMode())
const selectedMediaItem = ref<Media | undefined>()
const selectedComment = ref<Comment | undefined>()
const flaggedContent = ref<{ type: "media", content: Media } | { type: "comment", content: Comment } | undefined>()
const toast = useTemplateRef<ToastHtmlElement | null>("toast")
const scrollEndRef = useTemplateRef<HTMLSpanElement | null>("scrollEndRef")
const isScrollEndVisible = ref(false)

// Set up intersection observer for infinite scroll
useIntersectionObserver(
  scrollEndRef,
  ([{ isIntersecting }]) => {
    isScrollEndVisible.value = isIntersecting
  },
  {
    root: null,
    rootMargin: "200px",
    threshold: 0.1,
  },
)

watchEffect(() => {
  if (queryString.album && typeof queryString.album === "string") {
    emitSelectedAlbum(Number(queryString.album))
  }
})

watchEffect(() => {
  if (!queryString.mode) {
    mode.value = getInitialMode()
  }
})

watchEffect(() => {
  if (!isActive.value && queryString.upload && typeof queryString.upload === "string") {
    toggleUploader()
  }
})

const mediaTypeDefaults: MediaTypeFilter[] = ["pics", "vids"]

const { filters, update, count } = useFilters<IGalleryFilters>({
  perPage: 15,
  showGuestUploadsOnly: false,
  showPinnedUploadsOnly: false,
  mediaType: mediaTypeDefaults,
  tags: [],
})

const { filters: getAlbumFilters } = useFilters<IGetAlbumFilters>({
  include: ["settings"],
})

const { data: eventVault, isLoading: isLoadingEvent } = useGetEventVault(eventVaultId)

const { data: albums, isLoading: isLoadingAlbums } = useListAlbums(eventVaultId)

const { data: album, isLoading: isLoadingAlbum } = useGetAlbum(eventVaultId, albumId, getAlbumFilters)

const {
  data: media,
  fetchNextPage,
  isFetching,
  hasNextPage,
  isLoading: isLoadingMedia,
} = useInfiniteQuery({
  queryKey: ["listMedia", eventVaultId, albumId, filters],
  queryFn: pageParams =>
    listMedia(eventVaultId.value, albumId.value, {
      page: pageParams.pageParam ?? 1,
      perPage: filters.perPage,
      sortBy: filters.sortBy,
      orderBy: filters.orderBy,
      type: filters.mediaType?.length ? filters.mediaType.join(",") : undefined,
      showGuestUploadsOnly: filters.showGuestUploadsOnly,
      showPinnedUploadsOnly: filters.showPinnedUploadsOnly,
      tags: filters.tags,
      include: ["reactions", "tags"],
    }),
  getNextPageParam: (lastPage) => {
    if (lastPage.links?.Current === lastPage.links?.Last) {
      return undefined
    }

    const nextPage = Number(lastPage.links?.Next)

    return Number.isNaN(nextPage) ? undefined : nextPage
  },
  initialPageParam: 1,
})

const { mutate: pinMutation } = usePinMedia()

const isPinningMedia = ref<Media | undefined>()

function onPin(media?: Media) {
  if (!media || !eventVaultId?.value) {
    return
  }

  pinMutation({ eventVaultId: eventVaultId.value, isPinned: !media.isPinned, media: [media] }, {
    async onSuccess() {
      isPinningMedia.value = media
      await queryClient.invalidateQueries({ queryKey: ["listMedia"] }).then(() => {
        isPinningMedia.value = undefined
        const content = media.isPinned ? t("gallery_page.remove_favorite_success") : t("gallery_page.add_favorite_success")
        toast.value?.showToast({ title: t("success"), content, icon: "CircleCheck" })
      })
    },
  })
}

async function refreshMedia() {
  await queryClient.invalidateQueries({ queryKey: ["listMedia"] })
}

async function onUploadMediaSuccess() {
  pendingApprovalBannerDismissed.value = false
  await refreshMedia()
}

const mediaItems = computed<Media[]>(() => {
  return media.value?.pages.flatMap(p => p.media) ?? []
})

const { media: lightboxMedia, show } = useLightbox({ media: mediaItems })

watchEffect(() => (lightboxMedia.value = mediaItems.value))

function onView(index: number) {
  show(index + 1)
}

const canFetchMore = computed(() => !isLoadingMedia.value && mediaItems.value?.length && hasNextPage?.value)

function onFetchMore(): void {
  if (canFetchMore.value)
    fetchNextPage()
}

watchEffect(() => {
  if (canFetchMore.value && isScrollEndVisible.value && !isFetching.value)
    fetchNextPage()
})

const { isUploadsEnabled, isMainGalleryEnabled } = useGalleryPermissions(eventVault, album, {
  isHost: () => eventVaultStore.isHost ?? false,
})

function onAlbumSelect(album: Album) {
  if (album.isLocked && !eventVaultStore.isHost) {
    router.push(`/Guest/Unlock/${eventVaultId.value}/album/${albumId.value}?redirect=Gallery`)
    return
  }

  albumId.value = album.id
  queryString.album = album.id
}

function onModeChange(selectedMode: GalleryMode) {
  mode.value = selectedMode
  queryString.mode = selectedMode
  galleryModePreference.value = selectedMode
}

function onUploaderClose() {
  delete queryString.upload
}

function onUploaderOpen() {
  queryString.upload = true
}

function onViewMediaComments(media?: Media) {
  selectedMediaItem.value = media
  toggleCommentDrawer()
}

function onFlagMedia(media?: Media) {
  if (media) {
    flaggedContent.value = { type: "media", content: media }
    selectedMediaItem.value = media
    toggleFlagContentModal()
  }
}

function onFlagComment(comment?: Comment) {
  if (comment) {
    flaggedContent.value = { type: "comment", content: comment }
    selectedComment.value = comment
    toggleFlagContentModal()
  }
}

function clearSelectedContent() {
  selectedComment.value = undefined
  selectedMediaItem.value = undefined
  flaggedContent.value = undefined
}

// perhaps refactor this into a useTagging composable
const isTaggingEnabled = computed(() => eventVault.value?.eventVaultSettings?.taggingBehavior === TaggingBehavior.On)

// perhaps refactor this inot a useReactions composable
const isReactionsEnabled = computed(() => {
  const socialBehavior = eventVault.value?.eventVaultSettings?.socialsBehavior ?? SocialsBehavior.Disabled
  return [SocialsBehavior.ReactionsOnly, SocialsBehavior.ReactionsAndComments].includes(socialBehavior)
})

const isLoading = computed(() => {
  return Boolean(isLoadingEvent.value || isLoadingAlbums.value || isLoadingMedia.value || isLoadingAlbum.value)
})

const doesUserHavePendingApproval = computed(() => (media.value?.pages[0]?.meta?.totalAwaitingApproval ?? 0) > 0)

const canUploadVideos = computed(() => eventVault.value?.eventVaultSettings?.videoEnabled && !eventVault.value?.freemiumMode)

const pendingApprovalCount = computed(() => media.value?.pages[0]?.meta?.totalAwaitingApproval ?? 0)
</script>

<template>
  <GuestLayout hide-footer>
    <div class="gpx-gallery-page">
      <div class="gpx-gallery-page-albums">
        <AlbumSelector
          v-if="eventVaultId && eventVault?.eventVaultSettings?.albumsEnabled"
          v-model="albumId"
          :albums="albums"
          :hide-main-gallery="eventVault.eventVaultSettings?.hideMainGallery && !eventVaultStore.isHost"
          :is-loading="isLoadingAlbums"
          :event-vault-id="eventVaultId"
          :event-cover-image="eventVault.coverImageUrl"
          @select="onAlbumSelect"
        />
      </div>
      <div class="gpx-gallery-page-content-container w-full">
        <MainGalleryHiddenMessage
          v-if="!album && !isMainGalleryEnabled"
          :page-message="t('main_gallery_hidden_message.gallery_message')"
        />
        <div v-else class="gpx-gallery-page-content container">
          <GalleryActions
            :is-host="eventVaultStore.isHost"
            :event-vault="eventVault"
            :display-upload="isUploadsEnabled"
            :is-loading="isLoading"
            :is-loading-event="isLoadingEvent"
            :gallery-mode="mode"
            :pending-media-count="media?.pages[0]?.meta?.pendingMediaCount"
            :active-filters-count="count"
            @upload="toggleUploader"
            @refresh="refreshMedia"
            @filter="toggleFilters"
            @change-mode="onModeChange"
          />

          <Banner
            v-if="doesUserHavePendingApproval && !pendingApprovalBannerDismissed"
            class="mb-4"
            :variant="eventVaultStore.isHost ? 'warning' : 'default'"
            :description="t(eventVaultStore.isHost ? 'gallery_page.pending_approval_host' : 'gallery_page.pending_approval_guest', { count: pendingApprovalCount })"
            :prefix-icon="eventVaultStore.isHost ? 'Clock' : 'CircleInfo'"
            :closable="!eventVaultStore.isHost"
            @close="dismissPendingApprovalBanner"
          />

          <div>
            <div v-if="eventVaultId && media">
              <transition name="gpx-fade" mode="out-in">
                <div v-if="!mediaItems.length" class="gpx-gallery-grid_empty">
                  <h4>{{ t("gallery_grid.no_media_title") }}</h4>
                  <p>{{ t("gallery_grid.no_media_message") }}</p>
                  <AppLink
                    v-if="eventVaultStore.isHost && eventVaultId"
                    :to="`/event/${eventVaultId}/manage-media`"
                    class="gpx-button gpx-button_secondary"
                    target="_blank"
                  >
                    {{ t("gallery_grid.manage_media_button") }}
                  </AppLink>
                </div>

                <GalleryFeed
                  v-else-if="mode === GalleryMode.feed"
                  :event-settings="eventVault?.eventVaultSettings"
                  :media="mediaItems"
                  @view="onView"
                  @view-comments="onViewMediaComments"
                />

                <GalleryGrid v-else :items="mediaItems" :is-host="eventVaultStore.isHost" @view="onView" />
              </transition>
              <span v-if="mediaItems.length" ref="scrollEndRef" />
            </div>
            <div v-if="isLoadingMedia || isFetching" class="gpx-gallery-page-media-loading">
              <img src="@/assets/gpx-loading.gif" alt="Loading Spinner">
            </div>
          </div>
        </div>

        <Footer
          :custom-logo="templateStore.customFooterImage"
          :custom-footer-links="templateStore.customFooterLinks"
          :is-freemium-event="eventVault?.freemiumMode"
        />
      </div>
    </div>

    <Lightbox
      :is-host="eventVaultStore.isHost"
      :total-media-count-display="(media?.pages[0]?.meta?.totalPhotos ?? 0) + (media?.pages[0]?.meta?.totalVideos ?? 0)"
      :is-fetching="isFetching"
      :is-pinning="isPinningMedia && isFetching"
      @favourite="onPin"
      @fetch-more="onFetchMore"
      @view-comments="onViewMediaComments"
      @flag="onFlagMedia"
    />
    <CommentsDrawer v-if="selectedMediaItem" :media="selectedMediaItem" :is-active="isCommentDrawerActive" @flag="onFlagComment" />
    <Toast ref="toast" variant="success" />
    <GalleryFiltersDrawer
      :event-vault-id="eventVaultId"
      :filters="filters"
      :tags-enabled="isTaggingEnabled"
      :reactions-enabled="isReactionsEnabled"
      @update:filters="update"
    />
    <Drawer
      :name="UploadDrawerKey"
      :title="t('upload')"
      keep-alive
      @open="onUploaderOpen"
      @close="onUploaderClose"
    >
      <Uploader
        auto-proceed
        :can-upload-videos="canUploadVideos"
        :disabled="eventVault?.eventVaultSettings?.hideMainGallery && !albumId"
        :direct-uploads="eventVault?.directUpload"
        @success="onUploadMediaSuccess"
      />
    </Drawer>

    <FlagContentModal
      v-if="flaggedContent"
      :event-vault-id="eventVaultId"
      :content="flaggedContent"
      @cancel="clearSelectedContent"
      @success="clearSelectedContent"
      @error="clearSelectedContent"
    />
  </GuestLayout>
</template>

<style scoped>
.gpx-gallery-page {
  display: flex;
  flex-direction: column;
}

.gpx-gallery-page-content {
  padding-bottom: 3rem;
}

.gpx-gallery-page-media-loading {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 1.5rem;
}

.gpx-gallery-page-infinite-actions {
  display: flex;
  margin-top: 1.5rem;
  justify-content: center;
}

.gpx-gallery-grid_empty {
  min-height: 75vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 2rem;
}

.gpx-gallery-grid_empty h4,
.gpx-gallery-grid_empty p {
  padding: 0;
  margin: 0;
}

@media (width >= 768px) {
  .gpx-gallery-page-content-container {
    overflow: scroll;
  }

  .gpx-gallery-page-content-container,
  .gpx-gallery-page-albums {
    height: calc(100vh - var(--gpx-navbar-height));
  }

  .gpx-gallery-page {
    display: grid;
    grid-template-columns: fit-content(100%) auto;
  }

  .gpx-gallery-page-albums {
    display: flex;
  }
}
</style>
